import React, { useState, useRef } from 'react';
import { Dropdown, Checkbox, Radio, Bubble, SearchBox } from 'tea-component';
import { ALL_VALUE } from './constants';
import useIndex from './hooks/useIndex';
import useSearch from './hooks/useSearch';
import { SelectorMultipleProps } from './types';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { List as VirtualizedList, AutoSizer } from 'react-virtualized';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import '@/pages/dashboard/assets/scss/page/components/selectCompoents.scss';

const ROW_HEIGHT = 36;
const MAX_VISIBLE_ROWS = 10; // 根据需要显示的行数进行调整
const SelectorMultiple = (props: SelectorMultipleProps) => {
  const {
    label,
    buttonLabel = label,
    className = '',
    style,
    allDescribe = '',
    allInfo = '',
    showTips = false,
    tipsInfo = '',
    disabled = false,
  } = props;

  const bindClassName = useRef(
    `selector${Math.round(Math.random() * 10000 * Date.now()).toString(16)}`,
  );
  const {
    value,
    isOpen,
    options: propOptions,
    checkedAll,
    handleAll,
    handleOpen,
    handleClose,
    handleChange,
    handleRadioChange,
  } = useIndex(props, bindClassName.current);
  const { getLocalText } = useLocal('selector_multiple_component');
  const [isHover, setIsHover] = useState(false);
  const [hoverItem, setHoverItem] = useState('');
  const {
    searchValue,
    replaceMatch,
    buildHighLightData,
    handleSearchValueChange,
  } = useSearch(propOptions);
  const renderOptions = buildHighLightData() ?? [];
  // all 的查询匹配字符串
  const allMatchText = replaceMatch(allDescribe, searchValue);
  const isMatchData = renderOptions?.length > 0 && searchValue?.length > 0;
  const isMatchAll = allMatchText !== allDescribe;
  // 是否搜索为空
  const isMatch = isMatchAll || isMatchData;
  // 是否为清除按钮
  const isClear = renderOptions?.filter(v => !v.isAll)?.some((item) => value?.includes(item?.value));
  const listRef = useRef(null);
  const button: React.ReactNode = (() => {
    const tags: any[] = [];
    propOptions?.forEach((option) => {
      if (value?.includes(option.value)) {
        tags.push(option);
      }
    });
    const renderSelected = (tags: any[]) => {
      if (tags?.length > 2) return tags?.length;

      return (
        <div className='dbd-select-button-value-only-text'>
          {tags?.map((item, index) => {
            // 获取名称，过滤html代码
            const text = item?.text?.replace?.(/<[^>]+>/gim, '');
            const newText = `${text}${index !== tags.length - 1 ? ',' : ''}`;
            // 统一有图标则展示到选择框内
            return item?.icon ? <span key={index}>{item?.icon && <img src={item.icon} />} {newText}</span> : newText;
          })}
        </div>
      );
    };

    return (
      <div className={`selector-multiple__header select-button`}>
        <span className="select-button-text dbd-select-button-text">
          {buttonLabel ? buttonLabel : ''}&nbsp;
        </span>
        <span className='select-button-value'>
          {checkedAll ? (
            allDescribe
          ) : (
            // 和后端罗超核对后，后端可能会特意传空，所以这里不能用allDescribe,只能给空
            <>{tags.length ? renderSelected(tags) : ''}</>
          )}
        </span>
      </div>
    );
  })();

  const overallDom = <div className="select-context">
    {isMatchAll && (
      <>
        <div className="select-header-title">
          <span className="label">{getLocalText('overall')}</span>
        </div>
        <div className="select-header-felx">
          <Radio.Group
            onChange={handleRadioChange}
            value={checkedAll ? ALL_VALUE : ''}
          >
            <Radio name={ALL_VALUE}>
              <span
                dangerouslySetInnerHTML={{ __html: `${allMatchText}` }}
              />
              <div
                className="select-header-icon-box"
                onMouseOver={() => {
                  setIsHover(true);
                  setHoverItem('all');
                }}
                onMouseOut={() => {
                  setIsHover(false);
                  setHoverItem('');
                }}
              >
                {' '}
                <Bubble
                  trigger="hover"
                  arrowPointAtCenter
                  placement="top-start"
                  content={allInfo}
                  dark
                >
                  {/* 此处必须包一层，不然点击不生效 */}
                  <i>
                    <SvgIcon
                      className="select-header-ic"
                      type={
                        isHover && hoverItem === 'all'
                          ? 'alert-circle-action'
                          : 'alert-circle-m'
                      }
                    />
                  </i>
                </Bubble>
              </div>
            </Radio>
          </Radio.Group>
        </div>
      </>
    )}
    <>
      {renderOptions?.length > 0 && (
        <div className="select-felx">
          <span className="select-felx-lf">{label}</span>
          <span
            onClick={() => handleAll(renderOptions, isClear)}
            className="select-felx-rt"
          >
            {isClear
              ? getLocalText('clear_all')
              : getLocalText('select_all')}
          </span>
        </div>
      )}
    </>
  </div>;

  return (
    <Bubble
      // className='dbd-tips-select-info'
      dark
      escapeWithReference
      flipped={false}
      destroyOnClose={false}
      content={showTips && !isOpen ? tipsInfo : null}
    >
      <Dropdown
        clickClose={false}
        className={`dbd-new-select-dashboard-box ${bindClassName.current} ${className}`}
        style={{ marginRight: 10, ...style }}
        button={button}
        appearance="button"
        onOpen={() => {
          // 清空操作放在close中会导致结果闪动的问题，所以放在open方法中处理
          handleSearchValueChange('');
          handleOpen();
        }}
        onClose={(handleClose)}
        closeOnScroll={false}
        positionFixed={false}
        popupContainer={() =>
          document.querySelector(`.${bindClassName.current}`)
        }
        boxClassName="selector-multiple__dropdown"
        overlayClassName={'selector-multiple__dropdown-overlay'}
        disabled={disabled}
      >
        <SearchBox
          placeholder={getLocalText('placeholder')}
          className="selector-multiple-search-input"
          onChange={handleSearchValueChange}
          value={searchValue}
        />
        {isMatch ? (<div className="selector-multiple__dropdown-inner">
          <div className="list-container">
            <div className="tea-form-checks">
              {overallDom}
              <Checkbox.Group
                layout="column"
                value={value}
                className={`list dbd-selector-list`}
                style={{ height: `${(renderOptions.length) * ROW_HEIGHT}px` }}
                onChange={handleChange}
              >
                <AutoSizer>
                  {({ width, height }) => {
                    // 根据可用高度计算可见行数
                    const visibleRowCount = Math.min(
                      Math.floor(height / ROW_HEIGHT),
                      MAX_VISIBLE_ROWS
                    );
                    // 根据可见行数计算 VirtualizedList 的高度
                    const listHeight = visibleRowCount * ROW_HEIGHT;
                    return (
                      <VirtualizedList
                        rowCount={renderOptions.length}
                        ref={listRef}
                        rowRenderer={({ index, style }) => {
                          const item = renderOptions[index];
                          return (
                            <>
                              <Checkbox
                                key={item.value}
                                name={item.value}
                                style={style}
                              >
                                {item.icon && <img src={item.icon} />}
                                <span
                                  className={item.icon ? 'dbd-tea-form-checks-span-has-icon' : ''}
                                  dangerouslySetInnerHTML={{
                                    __html: `${item.text}`,
                                  }}
                                />
                                {
                                  item?.content &&
                                  <div
                                    className="select-header-icon-box"
                                    onMouseOver={() => {
                                      setIsHover(true);
                                      setHoverItem(item.value);
                                    }}
                                    onMouseOut={() => {
                                      setIsHover(false);
                                      setHoverItem('');
                                    }}
                                  >
                                    {' '}
                                    <Bubble
                                      trigger="hover"
                                      arrowPointAtCenter
                                      placement="top-start"
                                      content={getLocalText(item.content, {}, false)}
                                      dark
                                    >
                                      {/* 此处必须包一层，不然点击不生效 */}
                                      <i>
                                        <SvgIcon
                                          className="select-header-ic"
                                          type={
                                            isHover && hoverItem === item.value
                                              ? 'alert-circle-action'
                                              : 'alert-circle-m'
                                          }
                                        />
                                      </i>
                                    </Bubble>
                                  </div>
                                }
                              </Checkbox>
                            </>
                          );
                        }}
                        width={width}
                        height={listHeight}
                        rowHeight={ROW_HEIGHT}
                        overscanRowCount={5}
                        scrollToIndex={0}
                      />
                    );
                  }}
                </AutoSizer>
              </Checkbox.Group>
            </div>
          </div>
        </div>) : <div className="new-selector-multiple__dropdown--noData dbd-new-selector-nodata">
          {getLocalText('no_data')}
        </div>}
      </Dropdown>
    </Bubble>
  );
};

export default SelectorMultiple;
